import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonSelect,
  DateAndTimePicker,
  Link as DSLink,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import { addYears } from 'date-fns';

import { CF_NEU_BLD, CF_SEC_MED } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';

import { DATE_CUSTOM_LABEL_KEY } from './constants';
import { type SodCustomFilterProps } from './SodCustomFilter';
import {
  getCustomRangeLabel,
  getDaysFromToday,
  shiftDateString,
} from './utils';

import './SodDateFilter.css';

interface FilterRefType extends HTMLDivElement {
  openDropdown: () => void;
}

/**
 * `SodDateFilter` is a custom dropdown menu component used to apply a date-based filter.
 * It allows users to either choose from predefined date ranges or select a custom start and end date.
 * The selected filter is then passed back through a callback.
 *
 * @component
 *
 * @param {SodCustomFilterProps} props - The props for the SodDateFilter component.
 * @param {Object} props.filter - Filter configuration object.
 * @param {string} props.filter.label - The label displayed on the trigger button.
 * @param {string} props.filter.value - The unique identifier for the filter.
 * @param {string} [props.filter.method] - The comparison method (e.g., BETWEEN).
 * @param {Array<{ label: string, value: string, isChecked: boolean }>} [props.filter.options] - Optional list of predefined date range options.
 * @param {Array<{ label: string, value: string, isChecked: boolean }>} props.activeOptions - List of currently active filter options.
 * @param {string} props.footerLink1Text - Text displayed for the "Remove Filter" footer link.
 * @param {string} props.footerLink2Text - Text displayed for the "Clear Selection" footer link.
 * @param {Function} props.handleOptionsClick - Callback invoked when an option is selected or a custom date range is applied.
 * @param {Function} props.handleFilterChange - Callback invoked when the "Remove Filter" link is clicked.
 *
 * @returns {ReactElement} The rendered `SodDateFilter` component.
 *
 * @example
 * <SodDateFilter
 *   filter={{
 *     label: 'Created Date',
 *     value: 'createdDate',
 *     method: 'BETWEEN',
 *     options: [
 *       { label: 'Last 7 Days', value: '7', isChecked: false },
 *       { label: 'Last 30 Days', value: '30', isChecked: false }
 *     ]
 *     activeOptions: [
 *       { label: 'Last 7 Days', value: '7', isChecked: true }
 *     ]
 *   }}
 *   footerLink1Text="Remove Filter"
 *   footerLink2Text="Clear Selection"
 *   handleOptionsClick={(value, method, selectedOptions) => console.log(value, method, selectedOptions)}
 *   handleFilterChange={(filter) => console.log('Filter removed', filter)}
 * />
 */
function SodDateFilter({
  filter,
  activeOptions,
  footerLink1Text,
  footerLink2Text,
  enabledFilterValue,
  setEnabledFilterValue,
  handleOptionsClick,
  handleFilterChange,
  isDarkTheme,
  isDisabled,
}: SodCustomFilterProps): ReactElement {
  const l = useInvertedLocalize();

  const [isClearBtnDisabled, setIsClearBtnDisabled] = useState(true);
  const [isOutsideClickEnabled, setIsOutsideClickEnabled] = useState(true);
  const [optionsMap, setOptionsMap] = useState(filter.options ?? []);
  const [startDate, setStartDate] = useState<Date | undefined | null>(null);
  const [endDate, setEndDate] = useState<Date | undefined | null>(null);

  const rangeMaxDate = addYears(new Date(), 100);

  const filterRef = useRef<FilterRefType | null>(null);

  useEffect(() => {
    const shouldOpenDropdown = Boolean(
      filterRef.current !== null &&
        enabledFilterValue &&
        filter.value === enabledFilterValue
    );

    if (shouldOpenDropdown && filterRef.current) {
      setTimeout(() => {
        filterRef.current?.openDropdown();

        if (setEnabledFilterValue) {
          setEnabledFilterValue(null);
        }
      }, 200);
    }
  }, [filter.value, enabledFilterValue, setEnabledFilterValue]);

  // Determine if  `Clear Selection` is enabled or not
  useEffect(() => {
    const isOptionTrue = optionsMap.some((option) => option.isChecked);

    if (isOptionTrue || startDate || endDate) {
      setIsClearBtnDisabled(false);

      return;
    }

    setIsClearBtnDisabled(true);
  }, [optionsMap, startDate, endDate]);

  // Maipulate options based on applied filters
  useEffect(() => {
    const customDate: (string | number | null)[] = JSON.parse(
      activeOptions.find((activeOption) =>
        activeOption.label.includes(DATE_CUSTOM_LABEL_KEY)
      )?.value ?? '[null, null]'
    ) as (string | number | null)[];

    const [customStartDateShift, customEndDateShift] = customDate;

    if (customStartDateShift !== null) {
      setStartDate(shiftDateString(Number(customStartDateShift)));
    } else {
      setStartDate(null);
    }

    if (customEndDateShift !== null) {
      setEndDate(shiftDateString(Number(customEndDateShift)));
    } else {
      setEndDate(null);
    }

    setOptionsMap((prev) =>
      prev.map((prevOption) => ({
        ...prevOption,
        isChecked: activeOptions.some(
          (activeOption) => activeOption.value === prevOption.value
        ),
      }))
    );
  }, [activeOptions]);

  const handleLocalCustomOptionClick = <T extends number | null>(
    startDays: T,
    endDays: T
  ) => {
    const label = getCustomRangeLabel(startDays, endDays, 'MMM dd, yyyy');

    handleOptionsClick(filter.value, filter.method, [
      {
        label,
        value: `[${startDays ?? 'null'},${endDays ?? 'null'}]`,
        isChecked: true,
      },
    ]);
  };

  const customLabel = useMemo(() => {
    const { label } = filter;

    if (activeOptions.length !== 0) {
      const labelArray = activeOptions.map((option) => option.label).join(', ');
      const labelText = `${label}: `;

      return (
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color={CF_SEC_MED}>
          <span className='SodCustomFilter-btnLabel'>{labelText}</span>
          {labelArray}
        </Typography>
      );
    }

    return label;
  }, [activeOptions, filter]);

  return (
    <Menu
      ref={filterRef}
      key={filter.value}
      isOutsideClickEnabled={isOutsideClickEnabled}
      CustomMenu={
        <>
          <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
            {optionsMap.map((option) => (
              <MenuItem
                key={option.value}
                option={{
                  label: option.label,
                  value: option.value,
                  isChecked: option.isChecked,
                }}
                onClick={() => {
                  handleOptionsClick(filter.value, filter.method, [
                    {
                      label: option.label,
                      value: option.value,
                      isChecked: true,
                    },
                  ]);
                }}
                tabIndex={0}
              />
            ))}
            <Box tag='div' className='SodDateFilter-datePicker'>
              <DateAndTimePicker
                kind='dateStart'
                label={l('State Date')}
                startDate={startDate}
                setStartDate={(date: Date) => {
                  const startDays = getDaysFromToday(date);
                  const endDays = getDaysFromToday(endDate);

                  handleLocalCustomOptionClick(startDays, endDays);
                }}
                endDate={endDate}
                setEndDate={setEndDate}
                maxDate={rangeMaxDate}
                minDate={new Date(0, 0, 1)}
                onCalendarClose={() => {
                  setIsOutsideClickEnabled(true);
                }}
                onCalendarOpen={() => {
                  setIsOutsideClickEnabled(false);
                }}
                portalId='root'
                classNames={{
                  containerClassName: isDarkTheme
                    ? 'SodDateFilter-datePickerDropdown--isDarkTheme'
                    : '',
                }}
              />
              <Typography
                kind='body3'
                desktop='body3-d'
                mobile='body3-d'
                color={CF_NEU_BLD}>
                {l('to')}
              </Typography>
              <DateAndTimePicker
                kind='dateEnd'
                label={l('Last Date')}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={(date: Date) => {
                  const startDays = getDaysFromToday(startDate);
                  const endDays = getDaysFromToday(date);

                  handleLocalCustomOptionClick(startDays, endDays);
                }}
                maxDate={rangeMaxDate}
                minDate={new Date(0, 0, 1)}
                onCalendarClose={() => {
                  setIsOutsideClickEnabled(true);
                }}
                onCalendarOpen={() => {
                  setIsOutsideClickEnabled(false);
                }}
                portalId='root'
                classNames={{
                  containerClassName: isDarkTheme
                    ? 'SodDateFilter-datePickerDropdown--isDarkTheme'
                    : '',
                }}
              />
            </Box>
          </ul>
          <Box tag='div' className='SodRiskScoreFilter-footer'>
            <DSLink
              text={l(footerLink1Text)}
              size='small'
              kind='button'
              onClick={() => {
                handleFilterChange({
                  value: filter.value,
                  isChecked: true,
                  method: filter.method,
                });
              }}
            />
            <DSLink
              text={l(footerLink2Text)}
              isDisabled={isClearBtnDisabled}
              size='small'
              kind='button'
              onClick={() => {
                handleOptionsClick(filter.value, filter.method, []);
              }}
            />
          </Box>
        </>
      }
      label={l(filter.label)}
      trigger={
        <ButtonSelect kind='pill' label={customLabel} isDisabled={isDisabled} />
      }
    />
  );
}

SodDateFilter.defaultProps = {
  enabledFilterValue: null,
  setEnabledFilterValue: () => {},
  isDarkTheme: false,
  isDisabled: false,
};

export default memo(SodDateFilter);
